import { Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import { Activity, ArrowLeft, ClipboardList, History, type LucideIcon, Network } from 'lucide-react';
import { type ComponentType, lazy, Suspense, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NotFoundPanel } from '@/components/feedback/not-found-panel';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { AlertStatusActions } from '@/features/market-surveillance/alert-status-actions';
import { AlertSummaryHeader } from '@/features/market-surveillance/alert-summary-header';
import { useSurveillanceAlert } from '@/hooks/queries/market-surveillance/use-surveillance-alert';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { getErrorMessage, isNotFoundError } from '@/lib/api/error';
import type { SurveillanceAlertDetail } from '@/services/api/market-surveillance';
import { shortUuid } from '@/utils/string';

export interface SurveillanceTabProps {
  alertId: string;
  detail: SurveillanceAlertDetail;
}

const OverviewTab = lazy(() => import('@/features/market-surveillance/tabs/overview-tab'));
const InvestigationTab = lazy(() => import('@/features/market-surveillance/tabs/investigation-tab'));
const RelatedTab = lazy(() => import('@/features/market-surveillance/tabs/related-tab'));
const AuditTab = lazy(() => import('@/features/market-surveillance/tabs/audit-tab'));

interface SurveillanceTabDefinition {
  value: string;
  label: string;
  icon: LucideIcon;
  Component: ComponentType<SurveillanceTabProps>;
}

const DEFAULT_TAB = 'overview';

const SURVEILLANCE_TABS: readonly SurveillanceTabDefinition[] = [
  { value: DEFAULT_TAB, label: 'Overview', icon: ClipboardList, Component: OverviewTab },
  { value: 'investigation', label: 'Investigation', icon: Activity, Component: InvestigationTab },
  { value: 'related', label: 'Related', icon: Network, Component: RelatedTab },
  { value: 'audit', label: 'Audit', icon: History, Component: AuditTab },
];

const resolveTab = (param: string | null): string => (param && SURVEILLANCE_TABS.some((tab) => tab.value === param) ? param : DEFAULT_TAB);

const ALERT_NOT_FOUND = { title: 'Alert not found', message: 'No surveillance alert exists with this id.' };

interface AlertPageProps {
  alertId: string;
}

export const AlertPage = ({ alertId }: AlertPageProps) => {
  useDocumentTitle(`Alert ${shortUuid(alertId)}`);

  const query = useSurveillanceAlert(alertId);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?tab= is the single source of truth; unknown values resolve to the default (user-page pattern)
  const activeTab = resolveTab(searchParams.get('tab'));

  // Lazy per-tab mounting: a panel mounts on first activation, then stays mounted
  // (hidden via CSS) so tab switches never lose in-progress composer state
  const [activatedTabs, setActivatedTabs] = useState<ReadonlySet<string>>(() => new Set([activeTab]));
  useEffect(() => {
    setActivatedTabs((previous) => (previous.has(activeTab) ? previous : new Set([...previous, activeTab])));
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === DEFAULT_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', value);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to="/compliance/market-surveillance">
        <ArrowLeft className="size-4" />
        Back to alerts
      </Link>

      {query.isPending ? (
        <LoadingPanel />
      ) : query.isError && !query.data ? (
        // Full-page states only when we have nothing cached to show
        isNotFoundError(query.error) ? (
          <NotFoundPanel {...ALERT_NOT_FOUND} />
        ) : (
          <QueryErrorBanner message={`Failed to load alert: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
        )
      ) : query.data === undefined ? (
        <NotFoundPanel {...ALERT_NOT_FOUND} />
      ) : (
        <>
          {query.isError ? (
            // Background refetch failed (replica lag 404s included) — keep the loaded page and
            // any in-progress note; surface the error inline instead of tearing down the tabs
            <QueryErrorBanner message={`Failed to refresh alert: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
          ) : null}
          <AlertSummaryHeader detail={query.data} />
          <AlertStatusActions alertId={alertId} detail={query.data} />
          <Tabs onValueChange={handleTabChange} value={activeTab}>
            <TabsList>
              {SURVEILLANCE_TABS.map(({ value, label, icon: Icon }) => (
                <TabsTrigger className="gap-2" key={value} value={value}>
                  <Icon className="size-4" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
            {SURVEILLANCE_TABS.filter((tab) => activatedTabs.has(tab.value)).map(({ value, Component }) => (
              <TabsContent className="mt-4 data-[state=inactive]:hidden" forceMount key={value} value={value}>
                <Suspense fallback={<LoadingPanel />}>
                  <Component alertId={alertId} detail={query.data} />
                </Suspense>
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
};
AlertPage.displayName = 'AlertPage';
